import { cn, Button } from "@babybjorn/ui";
import { useTranslation } from "gatsby-plugin-react-i18next";
import {
	useState,
	type FC,
	type ImgHTMLAttributes,
	type PropsWithChildren,
} from "react";
import Image from "../../../components/Image";
import Badge from "../../../components/general/Badge";
import { useIsTablet } from "../../../hooks/use-is-screensize";
import useScrollBlock from "../../../hooks/useScrollBlock";
import type { MouseClickEventHandler } from "../../../type-definitions/utility-types";
import VideoModal from "../VideoModal";
import Icon from "../Icon";
import * as s from "./ImageContainer.module.scss";

export type VimeoResponse = {
	readonly title: string | null;
	readonly video_id: number | null;
	readonly description: string | null;
	readonly thumbnail_url: string | null;
	readonly width: number | null;
	readonly height: number | null;
};

export const validImageRatio = [
	"square",
	"portrait",
	"landscape",
	"wide",
	"original",
] as const;

type ImageRatio = (typeof validImageRatio)[number];

type ImageContainerProps = ImgHTMLAttributes<HTMLImageElement> &
	PropsWithChildren<{
		alt?: string;
		ratio?: ImageRatio;
		imgSrc?: string;
		videoSrc?: string;
		youtubeEmbedId?: string;
		vimeoResponse?: VimeoResponse;
		withPlayButton?: boolean;
		badge?: string;
		objectFit?: "contain" | "cover";
		fullHeight?: boolean;
		addPadding?: boolean;
		paddingVariant?: "gallery" | "productCard" | "bundleProductCard"; //Since the same product variant image is used in both sliders and galleries. We have to control which type of padding it gets.
		className?: string;
		onClick?: MouseClickEventHandler;
		width?: number;
		sizes?: string;
		priority?: boolean;
		hidePlaceholder?: boolean;
		hideZoomButton?: boolean;
		aspectRatio?: number;
	}>;

const ImageContainer: FC<ImageContainerProps> = ({
	alt,
	ratio,
	imgSrc,
	videoSrc,
	youtubeEmbedId,
	vimeoResponse,
	withPlayButton = false,
	badge,
	objectFit = "contain",
	fullHeight = false,
	className,
	onClick,
	loading,
	children,
	addPadding = false,
	paddingVariant = "productCard",
	priority = false,
	hidePlaceholder = false,
	hideZoomButton = false,
	...props
}) => {
	const { t } = useTranslation();
	const [isVideoModalOpen, setIsVideoModalOpen] = useState(false);
	const [, setLocked] = useScrollBlock();

	const isTablet = useIsTablet();

	const onVideoClick: MouseClickEventHandler = (e) => {
		e.stopPropagation();
		setLocked(true);
		setIsVideoModalOpen(true);
	};

	const onVideoCloseClick: MouseClickEventHandler = (e) => {
		e.stopPropagation();
		setLocked(false);
		setIsVideoModalOpen(false);
	};

	return (
		<>
			<div
				className={cn(
					s.imageContainer,
					ratio && ratio !== "original" ? s[ratio] : undefined,
					className,
					{
						[s.fullHeight]: fullHeight,
					},
				)}
				{...(onClick && isTablet ? { onClick } : {})}
			>
				{imgSrc && (
					<Image
						fill
						src={imgSrc}
						alt={alt}
						{...props}
						itemProp={
							videoSrc || youtubeEmbedId || vimeoResponse
								? "thumbnailUrl"
								: "image"
						}
						loading={loading}
						itemScope
						priority={priority}
						className={cn(s.image, {
							[s.moduleImage]: ratio === "square",
							[s.productCardPadding]:
								addPadding && paddingVariant === "productCard",
							[s.galleryPadding]: addPadding && paddingVariant === "gallery",
							[s.bundleProductCardPadding]:
								addPadding && paddingVariant === "bundleProductCard",
						})}
						style={{ objectFit }}
						hidePlaceholder={hidePlaceholder}
					/>
				)}
				{children}
				{((hideZoomButton && vimeoResponse) || withPlayButton) && (
					<Button
						className={cn(
							"absolute left-1/2 -translate-x-1/2 -translate-y-1/2",
							"top-[50.5%] h-12 w-12 md:top-[53%] md:min-h-20 md:min-w-20 lg:top-[50.5%] xl:top-[48%]",
						)}
						onClick={onVideoClick}
						aria-label={t("Play video")}
						variant="special"
						type="button"
					>
						<Icon icon="play" />
					</Button>
				)}
				{badge && <Badge labels={[badge]} />}
				{!hideZoomButton &&
					(onClick || videoSrc || youtubeEmbedId || vimeoResponse) && (
						<div className={s.buttons}>
							{onClick && !isTablet && !withPlayButton && (
								<Button
									onClick={
										videoSrc || youtubeEmbedId || vimeoResponse
											? onVideoClick
											: onClick
									}
									size="icon"
									variant="secondary"
									aria-label={
										videoSrc || youtubeEmbedId || vimeoResponse
											? t("View video")
											: t("Zoom in")
									}
								>
									<Icon
										icon={
											videoSrc || youtubeEmbedId || vimeoResponse
												? "video"
												: "zoom-in"
										}
									/>
								</Button>
							)}
						</div>
					)}
			</div>
			{(videoSrc || youtubeEmbedId || vimeoResponse) && (
				<VideoModal
					isOpen={isVideoModalOpen}
					videoSrc={videoSrc}
					youtubeEmbedId={youtubeEmbedId}
					vimeoResponse={vimeoResponse}
					onClose={onVideoCloseClick}
				/>
			)}
		</>
	);
};
export default ImageContainer;
